import React, { useEffect, useState } from "react";
import { Button, Spin } from "antd";
import { action, getMrn, getPayPalClientToken } from "../../api";
import { Base64 } from "js-base64";

type Props = {
  chargeByApple: Function;
  subscriptionData: Object;
  link: String;
  accountGid: String;
  successCallback: Function;
  failCallback: Function;
  recaptchaToken: String;
};

const PAYPAL_SDK = "https://www.paypal.com/sdk/js";
const currency = "USD";
const intent = "capture";

const scriptToHead = (attrs = {}) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// ✅ Check Apple Pay availability
const existsApplePayJsApi = async () => {
  try {
    return !!(
      window.ApplePaySession &&
      (await window.ApplePaySession.canMakePayments())
    );
  } catch {
    return false;
  }
};

const ApplePayButton = (props: Props) => {
  const {
    chargeByApple,
    subscriptionData,
    link,
    accountGid,
    successCallback,
    failCallback,
    recaptchaToken,
    payment,
  } = props;

  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ Merchant validation
  const handleValidateMerchant = (session) => async (event) => {
    try {
      console.log("🔄 Validating merchant...");
      const applepay = window.paypal.Applepay();

      const payload = await applepay.validateMerchant({
        validationUrl: event.validationURL,
        displayName: "My Shop",
      });

      console.log("✅ Merchant validated:", payload);

      session.completeMerchantValidation(payload.merchantSession);
    } catch (err) {
      console.error("❌ Merchant validation failed:", err);
      session.abort();
    }
  };

  // ✅ Payment Authorized
  const handlePaymentAuthorized = (session, resolve, reject) => {
    return async (event) => {
      try {
        const paymentData = event.payment;

        const customerName =
          (paymentData.billingContact?.givenName || "") +
          (paymentData.billingContact?.familyName || "");

        const deviceData = paymentData.token?.paymentData?.data || "";

        // Step 1: Create payment session
        const resEntity = await chargeByApple(
          {
            deviceData,
            amount: paymentData.amount,
            customerName,
          },
          recaptchaToken
        );

        const orderId = await getMrn(
          resEntity.paymentSessionGid,
          resEntity.psClientSecret
        );

        // Step 2: Confirm order
        await window.paypal.Applepay().confirmOrder({
          orderId,
          token: paymentData.token,
          billingContact: paymentData.billingContact,
        });

        // Step 3: Final action
        await action(resEntity.paymentSessionGid, resEntity.psClientSecret);

        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        successCallback(resEntity);

        resolve({ orderId });
      } catch (err) {
        console.error("❌ Apple Pay flow error:", err);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        await failCallback();
        reject(err);
      }
    };
  };

  // ✅ Payment request
  const getPaymentRequest = (amount) => {
    return {
      countryCode: payment?.currency?.countryAlpha2 || "US",
      currencyCode: payment?.currency?.name || "USD",
      total: {
        label: payment?.description || "Payment",
        amount: amount,
      },
      supportedNetworks: ["visa", "masterCard"],
      merchantCapabilities: ["supports3DS"],
    };
  };

  // ✅ Perform Apple Pay
  const performApplePayPayment = (amount) => {
    return new Promise((resolve, reject) => {
      try {
        const session = new window.ApplePaySession(
          3,
          getPaymentRequest(amount)
        );

        session.onvalidatemerchant = handleValidateMerchant(session);
        session.onpaymentauthorized = handlePaymentAuthorized(
          session,
          resolve,
          reject
        );
        session.oncancel = () => reject("Cancelled");

        session.begin();
      } catch (err) {
        console.error("❌ ApplePaySession error:", err);
        reject(err);
      }
    });
  };

  // ✅ Load SDK + eligibility
  const checkAppleEligible = async () => {
    setLoading(true);
    try {
      const enabled = await existsApplePayJsApi();
      if (!enabled || !accountGid) {
        setEligible(false);
        return;
      }

      const data = await getPayPalClientToken(accountGid);
      const decoded = JSON.parse(Base64.decode(data));

      await scriptToHead({
        src: `${PAYPAL_SDK}?client-id=${decoded.clientId}&merchant-id=${decoded.merchantId}&currency=${currency}&intent=${intent}&components=applepay`,
        "data-client-token": decoded?.token?.client_token,
      });

      const config = await window.paypal.Applepay().config();
      setEligible(config.isEligible);
    } catch (err) {
      console.error("❌ Apple Pay setup failed:", err);
      setEligible(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAppleEligible();
  }, []);

  // ✅ Click handler (FIXED amount)
  const handleClick = () => {
    const rawAmount = payment?.amount;

    if (!rawAmount) return;

    const payableAmount = (rawAmount / 100).toFixed(2);

    performApplePayPayment(payableAmount)
      .then((res) => console.log("✅ Success:", res))
      .catch((err) => console.error("❌ Failed:", err));
  };

  if (loading) {
    return <Spin spinning={loading} tip="Please wait..." />;
  }

  if (!eligible) {
    return <p>Not Eligible</p>;
  }

  return (
    <Button
      onClick={handleClick}
      style={{
        background: "#000",
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        padding: "10px 20px",
      }}
    >
       Pay
    </Button>
  );
};

export default ApplePayButton;